import { store } from '@redux/store';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import uiStateSlice from 'layout/redux/uiStateSlice';
import modalService from 'layout/services/modalService';
import { selectModal } from 'layout/redux/selectors';
import brochureLeadConstructor from '../services/leadConstructor';
import { postRecommendedLeadForm, updateModalProgress } from '../services/taskFlow';
import RecommendedButtonBasic from './recommendedButtonBasic';
import SkipButton from './skipButton';
import { closeModalSvg } from '../constants/svgIcons';
import { recommendedPolicyParagraph } from '../constants/sharedHtml';
import LegalText from './legalText';

export default class RecommendedScreenBasic {
  constructor(recommendationsHtml) {
    this.closeAttribute = '[data-close]';
    this.formId = 'get-recommended-communities';
    this.getBrochureButtonBasic = new RecommendedButtonBasic();
    const { id, guid } = selectModal(store.getState());
    this.id = id;
    this.guid = guid;
    this.onClickEvent = null;
    this.onSkipEvent = null;
    this.recommendationsHtml = recommendationsHtml;
    this.recommendedCheckAttribute = '[data-recommendation-check]';
    this.skipButton = new SkipButton();
    this.type = 'recommended';
    this.legalText = new LegalText(recommendedPolicyParagraph);
  }

  /**
   * Submits information for recommended communities selected by the user.
   * @param {*} event that triggered this action
   */
  submitRecommendationsForm(event) {
    event.preventDefault();

    const formData = new FormData(this.getForm());
    const brochureInformation = Object.fromEntries(formData.entries());
    const state = store.getState();
    const userProfile = state?.userProfile;
    brochureInformation.id = this.id;
    brochureInformation.type = this.type;

    // we fill the user information with
    // the data captured in the previous form.
    brochureInformation.email = userProfile.email;
    brochureInformation.postalCode = userProfile.zip;
    brochureInformation.international = userProfile.livesOutside;
    brochureInformation.phone = userProfile.phone;

    const userName = userProfile.name.split(' ');
    brochureInformation.firstName = userName[0];
    brochureInformation.lastName = userName.slice(1).join(" ").trim();
    brochureInformation.fromPage = state?.uiState?.modalInformation?.fromPage;
    brochureInformation.guid = this.guid;

    const brochureLeadObject = brochureLeadConstructor(brochureInformation);

    const modalInformation = this.getThankYouModalInformation(brochureLeadObject.recoComms);
    store.dispatch(uiStateSlice.actions.modalInformation(modalInformation));

    postRecommendedLeadForm(brochureLeadObject);
  }

  /**
   * Binds the events needed so recommended screen can respond to user interactions.
   */
  bindEvents() {
    this.onClickEvent = this.submitRecommendationsForm.bind(this);
    this.onSkipEvent = this.skipModal.bind(this);

    const getRecoBrochureElement = this.getBrochureButtonBasic.getNode();
    getRecoBrochureElement?.addEventListener('click', this.onClickEvent);

    const skipElement = this.skipButton.getNode();
    skipElement?.addEventListener('click', this.onSkipEvent);

    const closeElements = document.querySelectorAll(this.closeAttribute);
    closeElements.forEach((element) => element.addEventListener('click', RecommendedScreenBasic.closeModal));

    const recommendedElements = document.querySelectorAll(this.recommendedCheckAttribute);
    recommendedElements.forEach((elem) =>
      elem?.addEventListener('click', this.onRecommendationChecked.bind(this)));
    this.legalText.bindEvents();
  }

  /**
   * Unbinds the events needed so recommended screen can respond to user interactions.
   */
  unbindEvents() {
    this.skipButton.getNode().removeEventListener('click', this.onSkipEvent);
    this.getBrochureButtonBasic.getNode().removeEventListener('click', this.onClickEvent);
    const recommendedElements = document.querySelectorAll(this.recommendedCheckAttribute);
    recommendedElements.forEach((elem) => {
      elem.click = null
    });
    this.legalText.unbindEvents();
  }

  /**
   * Skips the recommended modal step since the user is not interested in any of the
   * recommendations provided.
   */
  skipModal(event) {
    event.preventDefault();
    const modalInformation = this.getThankYouModalInformation();
    modalInformation.googleAnalyticsDTO = new GoogleAnalyticsEvent("Lead Events", "Form Skip",
    "Search - LM - Dir Rec");
    updateModalProgress(modalInformation);
  }

  /**
   *
   * Validates if there's at least 1 recommendation selected. If that's not the case,
   * disables the brochure button.
   */
  onRecommendationChecked() {
    const checkedRecos = this.getForm().querySelectorAll(`${this.recommendedCheckAttribute}:checked`);
    if (checkedRecos.length === 0) {
      this.getBrochureButtonBasic.getNode().disabled = true;
      return;
    }

    this.getBrochureButtonBasic.getNode().disabled = false;
  }

  static closeModal() {
    modalService.clearHtml();
  }

  /**
   *
   * @returns the DOM element representing the recommendations form.
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  /**
   *
   * @returns an object with the information to move to thank you screen
   */
  getThankYouModalInformation(recommendedCommunities) {
    return {
      id: this.id,
      name: 'thankYouScreen',
      selectedRecommendedCommunities: recommendedCommunities,
    };
  }

  /**
   * Returns an array of unique recommended brands from the recommendationsHtml property.
   * @returns {string[]} An array of unique recommended brands.
   */
  getRecomendedBrands() {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.recommendationsHtml, 'text/html');
    const brandElements = doc.querySelectorAll('.result__brand');
    const brands = [];
    brandElements.forEach((el) => {
      const brand = el.textContent.trim();

      // Only count unique brands
      if (!brands.includes(brand)) {
        brands.push(brand);
      }
    });
    return brands;
  }

  /**
   *
   * @returns the HTML that represents the recommendations screen.
   */
  getHtml() {
    this.legalText.setReplacements([
      { key: "{0}", value: "I'm Interested"},
    ]);
    return `
    <div class="lead-form form--recos">
      <button class="modal__close" data-close data-close-recommended>
        ${closeModalSvg}          
      </button>
      <p class="form__title">Discover More Great Communities</p>
      <p class="form__subtitle">Select additional listings for more information</p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="carousel__container" data-recommendations-container>
          ${this.recommendationsHtml}
          </div>
          <div class="form__button-bar" data-skip>
            ${this.skipButton.getHtml()}
            ${this.getBrochureButtonBasic.getHtml()}
          </div>
        </fieldset>        
      </form>
      ${this.legalText.getHtml()}
    </div>`;
  }
}
